/**
 * SME Climate Intelligence Dashboard
 * Decision-grade screen: 10-second comprehension target.
 * Data sources: /api/baseline, /api/confidence, /api/actions — V1 only, no duplicate calcs.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import client from "@/api/client";
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine
} from "recharts";
import {
  TrendingDown, Zap, RefreshCw, ChevronDown, ArrowRight, Package,
  Shield, AlertTriangle, Info, BarChart2, Activity, Plus, Sparkles,
  Edit3, CheckCircle2, Target, DollarSign
} from "lucide-react";
import FinancingReadinessScore from "@/components/FinancingReadinessScore";
import CreditPotentialPanel from "@/components/CreditPotentialPanel";
import SMEDashboard from "@/pages/app/SMEDashboard";
import ActivationBanner from "@/components/ActivationBanner";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── Geography-aware energy pricing constants ──────────────────────────────
const GRID_FACTORS = { NG:0.44, ZA:0.95, KE:0.30, GH:0.38, ET:0.08, TZ:0.45, global:0.49 };
const DIESEL_PRICE = { NG:0.60, ZA:1.10, KE:0.95, GH:0.85, ET:0.80, global:0.90 };
const GRID_TARIFF  = { NG:0.07, ZA:0.12, KE:0.15, GH:0.13, ET:0.05, global:0.10 };
const DIESEL_EF    = 2.68; // kgCO2e per liter diesel
const MONTHS       = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const pick  = (d, k) => d[(k||"").toUpperCase?.()?.slice?.(0,2)] ?? d.global ?? 0;
const fmt   = (n, d=1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";
const fmtK  = (n) => !n ? "$0" : n>=1e6 ? `$${(n/1e6).toFixed(1)}M` : n>=1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Math.round(n)}`;
const capFst= (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// ── Derived calculations (lightweight, deterministic, traceable) ──────────
function calcEnergyCost(baseline) {
  if (!baseline) return { totalCost:0, gridCost:0, dieselCost:0, elecKwh:0, dieselLiters:0, gridTariff:0, dieselPrice:0 };
  const geo        = baseline?.assessment?.geography || "global";
  const gridFactor = pick(GRID_FACTORS, geo);
  const dieselPrc  = pick(DIESEL_PRICE, geo);
  const gridTrf    = pick(GRID_TARIFF, geo);
  const bd         = baseline?.emissions?.category_breakdown || {};
  const elecKwh    = gridFactor > 0 ? (bd.electricity?.kgco2e || 0) / gridFactor : 0;
  const dieselL    = DIESEL_EF  > 0 ? (bd.fuel?.kgco2e || 0) / DIESEL_EF           : 0;
  const gridCost   = elecKwh * gridTrf;
  const dieselCost = dieselL * dieselPrc;
  return { totalCost: gridCost + dieselCost, gridCost, dieselCost, elecKwh, dieselLiters: dieselL, gridTariff: gridTrf, dieselPrice: dieselPrc };
}

function calcCircularityScore(baseline) {
  const cats = Object.keys(baseline?.emissions?.category_breakdown || {});
  return Math.min(100, 20 + (cats.includes("waste")?30:0) + (cats.includes("materials")?30:0) + (cats.includes("transport")?20:0));
}

function buildMonthlyData(total, reduction) {
  const sf   = [1.05,1.05,1.0,0.95,0.95,1.0,1.05,1.05,1.0,0.95,1.0,1.05];
  const norm = sf.reduce((a,b)=>a+b,0) / 12;
  const after = Math.max(0, (total||0) - (reduction||0));
  return MONTHS.map((month,i) => ({
    month,
    baseline:    parseFloat(((total||0)   / 12 * sf[i] / norm).toFixed(2)),
    afterAction: reduction > 0 ? parseFloat((after / 12 * sf[i] / norm).toFixed(2)) : null,
  }));
}

function generateInsight(baseline) {
  const hs = baseline?.emissions?.hotspots || [];
  if (!hs.length) return null;
  const top = hs[0];
  const cat = top.category_label || capFst(top.category || "unknown");
  const pct = top.pct_total || 0;
  if (pct > 60) return `Your emissions are concentrated in ${cat} (${pct.toFixed(0)}%). This single source presents the highest cost and emissions reduction opportunity.`;
  if (pct > 30) return `Your emissions are primarily driven by ${cat} (${pct.toFixed(0)}%). Reducing this source first delivers the most significant financial and climate impact.`;
  return `Emissions are distributed across multiple sources. ${cat} leads at ${pct.toFixed(0)}%. A diversified reduction strategy is recommended.`;
}

// ── Reusable card wrapper ─────────────────────────────────────────────────
const Card = ({ children, className="" }) => (
  <div className={`bg-white border border-slate-200 rounded-xl ${className}`}>{children}</div>
);
const CardHeader = ({ title, sub, right }) => (
  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
    <div>
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
    {right}
  </div>
);
const EmptyState = ({ icon: Icon, text }) => (
  <div className="py-10 flex flex-col items-center justify-center text-center">
    <Icon size={28} className="text-slate-200 mb-2" />
    <p className="text-xs text-slate-400">{text}</p>
  </div>
);

// ── SECTION A: KPI Strip ──────────────────────────────────────────────────
function SmeKpiStrip({ baseline, confidence, energy }) {
  const [showEDetail, setShowEDetail] = useState(false);
  const totalT   = baseline?.emissions?.total_tco2e || 0;
  const circ     = calcCircularityScore(baseline);
  const conf     = confidence?.overall_score || 0;
  const confTier = confidence?.tier || null;
  const confColor = conf>=75 ? "text-[#059669]" : conf>=50 ? "text-blue-600" : conf>=40 ? "text-amber-600" : "text-red-500";
  const circColor = circ>=70 ? "text-[#059669]" : circ>=40 ? "text-amber-600" : "text-red-500";

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sme-kpi-strip">
      {/* Card 1: Carbon Footprint */}
      <Card>
        <div className="p-5">
          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
            <TrendingDown size={14} className="text-slate-600" />
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-bold tabular-nums text-slate-900 leading-none" data-testid="kpi-carbon">
              {totalT ? fmt(totalT, 1) : "—"}
            </span>
            <span className="text-sm text-slate-400 mb-0.5">tCO₂e</span>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Carbon Footprint</p>
          {baseline?.reporting_period?.year && <p className="text-xs text-slate-400 mt-0.5">FY{baseline.reporting_period.year}</p>}
        </div>
      </Card>

      {/* Card 2: Energy Cost (expandable) */}
      <Card className="cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200" >
        <div className="p-5" onClick={() => setShowEDetail(!showEDetail)} data-testid="kpi-energy-cost">
          <div className="flex items-start justify-between mb-3">
            <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center">
              <Zap size={14} className="text-amber-600" />
            </div>
            <ChevronDown size={12} className={`text-slate-400 transition-transform mt-1 ${showEDetail?"rotate-180":""}`} />
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-3xl font-bold tabular-nums text-amber-600 leading-none">
              {energy?.totalCost > 0 ? fmtK(energy.totalCost) : "—"}
            </span>
            <span className="text-xs text-slate-400 mb-0.5">/yr</span>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Energy Cost</p>
          <p className="text-xs text-slate-400 mt-0.5">diesel + grid</p>
          {showEDetail && energy?.totalCost > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5" data-testid="energy-detail">
              {[["Diesel", energy.dieselCost, `${fmt(energy.dieselLiters,0)}L × $${energy.dieselPrice}/L`],
                ["Grid", energy.gridCost, `${fmt(energy.elecKwh,0)} kWh × $${energy.gridTariff}/kWh`]].map(([lbl, cost, detail]) => (
                <div key={lbl} className="space-y-0.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">{lbl}</span>
                    <span className="font-semibold text-slate-700">{fmtK(cost)}/yr</span>
                  </div>
                  <p className="text-xs text-slate-400">{detail}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Card 3: Circularity Score */}
      <Card>
        <div className="p-5">
          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center mb-3">
            <RefreshCw size={14} className="text-[#059669]" />
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className={`text-3xl font-bold tabular-nums leading-none ${circColor}`} data-testid="kpi-circularity">
              {baseline ? circ : "—"}
            </span>
            <span className="text-xs text-slate-400 mb-0.5">/ 100</span>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Circularity Score</p>
          <p className="text-xs text-slate-400 mt-0.5">category coverage</p>
        </div>
      </Card>

      {/* Card 4: Data Confidence */}
      <Card>
        <div className="p-5">
          <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
            <Shield size={14} className="text-blue-600" />
          </div>
          <div className="flex items-end gap-1 mb-1">
            <span className={`text-3xl font-bold tabular-nums leading-none ${confColor}`} data-testid="kpi-confidence">
              {confidence ? fmt(conf, 0) : "—"}
            </span>
            <span className="text-xs text-slate-400 mb-0.5">%</span>
          </div>
          <p className="text-xs text-slate-400 uppercase tracking-wide">Data Confidence</p>
          {confTier && <p className="text-xs text-slate-400 mt-0.5 capitalize">{confTier}</p>}
        </div>
      </Card>
    </div>
  );
}

// ── SECTION B LEFT: Climate Snapshot ─────────────────────────────────────
function ClimateSnapshotCard({ baseline }) {
  const insight = generateInsight(baseline);
  const year    = baseline?.reporting_period?.year;
  const org     = baseline?.organization?.name;
  const total   = baseline?.emissions?.total_tco2e;

  return (
    <Card data-testid="climate-snapshot-card">
      <CardHeader title="Climate Snapshot" sub={year ? `FY${year} · ${org || ""}` : null}
        right={<span className="text-xs px-2 py-1 bg-slate-50 border border-slate-200 rounded-full text-slate-500">Deterministic</span>} />
      <div className="p-5">
        {insight ? (
          <>
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 mb-4">
              <Sparkles size={16} className="text-[#059669] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-900 leading-relaxed font-medium" data-testid="climate-insight">{insight}</p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {(baseline?.emissions?.hotspots || []).slice(0,3).map((hs, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">#{hs.rank} Source</p>
                  <p className="text-sm font-semibold text-slate-900 capitalize">{hs.category_label || hs.category}</p>
                  <p className="text-xs text-slate-400 tabular-nums">{fmt(hs.tco2e,2)} t · {fmt(hs.pct_total,0)}%</p>
                </div>
              ))}
            </div>
          </>
        ) : <EmptyState icon={Activity} text="Run calculations to generate your climate snapshot" />}
      </div>
    </Card>
  );
}

// ── SECTION B RIGHT: Scope Breakdown ─────────────────────────────────────
function ScopeBreakdownCard({ baseline }) {
  const [expanded, setExpanded] = useState(false);
  const bd    = baseline?.emissions?.scope_breakdown || {};
  const s1    = bd.scope_1?.tco2e || 0;
  const s2    = bd.scope_2?.tco2e || 0;
  const s3    = bd.scope_3?.tco2e || 0;
  const total = (s1+s2+s3) || 1;
  const catBd = baseline?.emissions?.category_breakdown || {};

  const donut = [
    { name:"Scope 1", value:s1, color:"#0F172A" },
    { name:"Scope 2", value:s2, color:"#059669" },
    { name:"Scope 3", value:s3, color:"#3B82F6" },
  ].filter(d => d.value > 0);

  const scopeMap = {
    "Scope 1": Object.entries(catBd).filter(([,v])=>v.scope===1),
    "Scope 2": Object.entries(catBd).filter(([,v])=>v.scope===2),
    "Scope 3": Object.entries(catBd).filter(([,v])=>v.scope===3),
  };

  return (
    <Card data-testid="scope-breakdown-card">
      <CardHeader title="Emissions by Scope" sub="GHG Protocol"
        right={<button onClick={()=>setExpanded(!expanded)} data-testid="scope-expand-btn"
          className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1">
          {expanded?"Collapse":"Expand"} <ChevronDown size={11} className={`transition-transform ${expanded?"rotate-180":""}`} />
        </button>} />
      <div className="p-5">
        {donut.length > 0 ? (
          <>
            <div className="flex gap-4 items-center">
              <div className="relative flex-shrink-0" style={{width:120,height:120}}>
                <PieChart width={120} height={120}>
                  <Pie data={donut} cx={58} cy={58} innerRadius={34} outerRadius={52} dataKey="value" paddingAngle={2} startAngle={90} endAngle={-270}>
                    {donut.map((d,i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-sm font-bold text-slate-900">{fmt(total,1)}</span>
                  <span className="text-xs text-slate-400">tCO₂e</span>
                </div>
              </div>
              <div className="flex-1 space-y-3">
                {[["Scope 1","Direct",s1,"#0F172A"],["Scope 2","Purchased energy",s2,"#059669"],["Scope 3","Value chain",s3,"#3B82F6"]].map(([lbl,desc,val,color])=>(
                  <div key={lbl}>
                    <div className="flex justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2 h-2 rounded-full" style={{backgroundColor:color}} />
                        <span className="text-xs font-medium text-slate-700">{lbl}</span>
                      </div>
                      <span className="text-xs font-bold tabular-nums text-slate-900">{fmt(val,1)}t <span className="font-normal text-slate-400">({fmt(val/total*100,0)}%)</span></span>
                    </div>
                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{width:`${val/total*100}%`,backgroundColor:color}} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {expanded && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-3" data-testid="scope-detail">
                {Object.entries(scopeMap).filter(([,cats])=>cats.length>0).map(([scope,cats])=>(
                  <div key={scope}>
                    <p className="text-xs font-semibold text-slate-500 mb-1.5">{scope}:</p>
                    {cats.map(([cat,v])=>(
                      <div key={cat} className="flex justify-between text-xs py-0.5 pl-3">
                        <span className="text-slate-500 capitalize">{cat}</span>
                        <span className="font-medium text-slate-700 tabular-nums">{fmt(v.tco2e,3)} t</span>
                      </div>
                    ))}
                  </div>
                ))}
                <p className="text-xs text-slate-400 italic">* Scope 3 estimated using industry benchmarks</p>
              </div>
            )}
          </>
        ) : <EmptyState icon={BarChart2} text="No scope data yet" />}
      </div>
    </Card>
  );
}

// ── SECTION C: Emission Hotspots ──────────────────────────────────────────
function HotspotsCard({ baseline }) {
  const catBd  = baseline?.emissions?.category_breakdown || {};
  const total  = baseline?.emissions?.total_tco2e || 1;
  const bars   = Object.entries(catBd)
    .map(([k,v]) => ({ name: capFst(k), value: v.tco2e||0, pct: (v.tco2e||0)/total*100 }))
    .filter(d => d.value > 0)
    .sort((a,b) => b.value - a.value)
    .slice(0, 6);

  const CustomTick = ({x, y, payload}) => (
    <text x={x-6} y={y+4} textAnchor="end" fill="#64748B" fontSize={11}>{payload.value}</text>
  );
  const CustomTooltip = ({active,payload}) => active && payload?.length ? (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-slate-900">{payload[0].payload.name}</p>
      <p className="text-[#059669]">{fmt(payload[0].value,3)} tCO₂e</p>
      <p className="text-slate-400">{fmt(payload[0].payload.pct,1)}% of total</p>
    </div>
  ) : null;

  return (
    <Card data-testid="hotspots-card">
      <CardHeader title="Emission Hotspots" sub="Sorted by tCO₂e — V1 source" />
      <div className="p-5">
        {bars.length > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={bars} layout="vertical" barSize={14} margin={{top:0,right:40,left:70,bottom:0}}>
              <CartesianGrid strokeDasharray="2 2" stroke="#F1F5F9" horizontal={false} />
              <XAxis type="number" tick={{fontSize:10,fill:"#94A3B8"}} axisLine={false} tickLine={false} tickFormatter={v=>`${fmt(v,1)}t`} />
              <YAxis type="category" dataKey="name" tick={<CustomTick/>} axisLine={false} tickLine={false} width={70} />
              <Tooltip content={<CustomTooltip/>} cursor={{fill:"#F8FAFC"}} />
              <Bar dataKey="value" radius={[0,4,4,0]}>
                {bars.map((d,i) => <Cell key={i} fill={i===0?"#0F172A":i===1?"#059669":"#94A3B8"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <EmptyState icon={BarChart2} text="Run calculations to see hotspots" />}
      </div>
    </Card>
  );
}

// ── SECTION D: Monthly Emissions Trend ───────────────────────────────────
function MonthlyTrendCard({ baseline, actions }) {
  const [view, setView] = useState("chart");
  const total     = baseline?.emissions?.total_tco2e || 0;
  const totalRed  = (actions||[]).reduce((s,a)=>s+(a.co2_reduction||0), 0);
  const monthly   = buildMonthlyData(total, totalRed);
  const hasAction = totalRed > 0;

  const CustomTooltip = ({active,payload,label}) => active && payload?.length ? (
    <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-slate-900 mb-1">{label}</p>
      {payload.map((p,i) => (
        <p key={i} style={{color:p.color}}>{p.name}: {fmt(p.value,2)} tCO₂e</p>
      ))}
    </div>
  ) : null;

  return (
    <Card data-testid="monthly-trend-card">
      <CardHeader title="Monthly Emissions Trend"
        sub={`Annual total: ${fmt(total,1)} tCO₂e${hasAction ? ` · After action: ${fmt(total-totalRed,1)} tCO₂e` : ""} — distributed evenly (seasonal ±5%)`}
        right={
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[["chart","Chart"],["table","Table"]].map(([v,l])=>(
              <button key={v} onClick={()=>setView(v)} data-testid={`trend-${v}-btn`}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${view===v?"bg-white text-slate-900 shadow-sm":"text-slate-500 hover:text-slate-700"}`}>
                {l}
              </button>
            ))}
          </div>
        } />
      <div className="p-5">
        {!total ? <EmptyState icon={Activity} text="Run calculations to see monthly trend" /> :
          view === "chart" ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthly} margin={{top:4,right:8,left:-10,bottom:0}}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="month" tick={{fontSize:11,fill:"#94A3B8"}} axisLine={false} tickLine={false} />
                  <YAxis tick={{fontSize:10,fill:"#94A3B8"}} axisLine={false} tickLine={false} tickFormatter={v=>`${fmt(v,1)}t`} />
                  <Tooltip content={<CustomTooltip/>} />
                  <Line type="monotone" dataKey="baseline" stroke="#0F172A" strokeWidth={2} dot={false} name="Baseline" />
                  {hasAction && <Line type="monotone" dataKey="afterAction" stroke="#059669" strokeWidth={2} dot={false} strokeDasharray="4 2" name="After Action" />}
                </LineChart>
              </ResponsiveContainer>
              {hasAction && (
                <div className="flex items-center gap-4 mt-3 text-xs">
                  <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-[#0F172A]"/><span className="text-slate-500">Baseline</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-[#059669] [border-style:dashed]" style={{borderTop:"2px dashed #059669",width:12,height:0}}/><span className="text-slate-500">After Action</span></div>
                  <span className="ml-auto text-slate-400">Reduction: <strong className="text-[#059669]">{fmt(totalRed,1)} tCO₂e/yr</strong></span>
                </div>
              )}
            </>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="trend-table">
                <thead><tr className="border-b border-slate-100">
                  {["Month","Baseline (t)","After Action (t)","Reduction (t)"].map(h=>(
                    <th key={h} className="text-left pb-2 text-slate-400 font-medium pr-4">{h}</th>
                  ))}</tr></thead>
                <tbody>{monthly.map((row)=>(
                  <tr key={row.month} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-1.5 pr-4 font-medium text-slate-700">{row.month}</td>
                    <td className="py-1.5 pr-4 tabular-nums text-slate-700">{fmt(row.baseline,2)}</td>
                    <td className="py-1.5 pr-4 tabular-nums text-[#059669]">{row.afterAction ? fmt(row.afterAction,2) : "—"}</td>
                    <td className="py-1.5 tabular-nums text-slate-500">{row.afterAction ? fmt(row.baseline-row.afterAction,2) : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )
        }
      </div>
    </Card>
  );
}

// ── SECTION E: Carbon Value Potential ────────────────────────────────────
function CarbonValueCard({ actions }) {
  const totalRed = (actions||[]).reduce((s,a)=>s+(a.co2_reduction||0), 0);
  const low  = totalRed * 5;
  const high = totalRed * 15;

  return (
    <Card data-testid="carbon-value-card">
      <CardHeader title="Carbon Reduction & Value Potential" sub="Source: Action Intelligence Engine" />
      <div className="p-5">
        {totalRed > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              {[
                { label:"Annual Reduction", value:`${fmt(totalRed,1)} tCO₂e`, color:"text-[#059669]" },
                { label:"Value Range (Low)",  value:`${fmtK(low)}/yr`,        color:"text-blue-600" },
                { label:"Value Range (High)", value:`${fmtK(high)}/yr`,       color:"text-indigo-600" },
              ].map(({label,value,color})=>(
                <div key={label} className="bg-slate-50 border border-slate-100 rounded-xl p-4 text-center">
                  <p className={`text-lg font-bold tabular-nums ${color}`}>{value}</p>
                  <p className="text-xs text-slate-400 mt-1 uppercase tracking-wide">{label}</p>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Info size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                <strong>Status: Indicative.</strong> Value range: $5–$15/tCO₂e (voluntary market). Carbon credits require project validation and certification before issuance.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Derivation</p>
              <p className="text-xs text-slate-400">
                Reduction ({fmt(totalRed,1)} tCO₂e) × $5 = {fmtK(low)} low estimate.
                × $15 = {fmtK(high)} high estimate. Source: Action Intelligence Engine.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <EmptyState icon={Sparkles} text="Generate action recommendations to see carbon value potential" />
            <p className="text-xs text-slate-400 text-center italic">Value range = reduction (tCO₂e) × $5–$15 (voluntary market)</p>
          </div>
        )}
      </div>
    </Card>
  );
}

// ── SECTION F: Data Confidence ────────────────────────────────────────────
function DataConfidenceCard({ confidence, assessmentId, navigate }) {
  const score = confidence?.overall_score || 0;
  const tier  = confidence?.tier || null;
  const color = score>=75?"#059669":score>=50?"#3B82F6":score>=40?"#D97706":"#DC2626";

  const bars = [
    { label:"Data Completeness",  key:"completeness",        weight:"25%" },
    { label:"Evidence Coverage",  key:"evidence_score",      weight:"25%" },
    { label:"Data Consistency",   key:"consistency",         weight:"20%" },
    { label:"Source Reliability", key:"source_reliability",  weight:"15%" },
    { label:"Benchmark Fit",      key:"benchmark_fit",       weight:"15%" },
  ];

  return (
    <Card data-testid="data-confidence-card">
      <CardHeader title="Data Confidence" sub="Source: /api/confidence" />
      <div className="p-5">
        {confidence ? (
          <>
            <div className="flex items-center gap-4 mb-5">
              <div className="relative flex-shrink-0" style={{width:80,height:80}}>
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="30" fill="none" stroke="#E2E8F0" strokeWidth="8" />
                  <circle cx="40" cy="40" r="30" fill="none" stroke={color} strokeWidth="8"
                    strokeDasharray={`${(score/100)*188.5} 188.5`}
                    strokeDashoffset="47.1" strokeLinecap="round"
                    style={{transition:"stroke-dasharray 1s ease"}} />
                  <text x="40" y="36" textAnchor="middle" fill="#0F172A" fontSize="14" fontWeight="700">{Math.round(score)}</text>
                  <text x="40" y="50" textAnchor="middle" fill="#94A3B8" fontSize="9">/ 100</text>
                </svg>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900 capitalize" data-testid="confidence-tier">{tier || "—"}</p>
                <p className="text-xs text-slate-400 mt-0.5 max-w-[180px]">{confidence.tier_label || "—"}</p>
              </div>
            </div>
            <div className="space-y-2.5">
              {bars.map(({label, key, weight}) => {
                const val = confidence[key] || 0;
                const barColor = val>=75?"#059669":val>=50?"#3B82F6":val>=40?"#D97706":"#DC2626";
                return (
                  <div key={key}>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-slate-500">{label} <span className="text-slate-300">{weight}</span></span>
                      <span className="text-xs font-bold tabular-nums" style={{color:barColor}}>{fmt(val,0)}%</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{width:`${val}%`,backgroundColor:barColor}} />
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={() => navigate(`/assessments/${assessmentId}`)}
              data-testid="view-intelligence-btn"
              className="mt-4 w-full text-xs text-[#059669] hover:text-emerald-700 font-medium flex items-center justify-center gap-1">
              View Full Intelligence <ArrowRight size={11} />
            </button>
          </>
        ) : <EmptyState icon={Shield} text="Run confidence assessment in the Intelligence tab" />}
      </div>
    </Card>
  );
}

// ── SECTION G: Action Preview ─────────────────────────────────────────────
const ACTION_LABELS = {
  solar_pv:"Solar PV", energy_efficiency:"Energy Efficiency", fuel_switch:"Fuel Switch",
  fleet_electrification:"Fleet Electrification", waste_reduction:"Waste Reduction", materials_optimization:"Low-Carbon Materials",
};

function ActionPreviewCard({ actions, assessmentId, navigate }) {
  const top = (actions||[])[0];
  if (!top) return (
    <Card data-testid="action-preview-card">
      <CardHeader title="Action Preview" sub="Source: Action Intelligence Engine" />
      <EmptyState icon={Sparkles} text="Generate actions in the Investment Pack tab" />
    </Card>
  );

  const CONF_COLOR = {high:"text-[#059669] bg-emerald-50",medium:"text-amber-600 bg-amber-50",low:"text-red-500 bg-red-50"};
  const conf = top.confidence_level || "medium";
  const notes = (top.derivation_notes||"").split("\n\nAssumptions:")[0];

  return (
    <Card data-testid="action-preview-card">
      <CardHeader title="Top Action Recommendation"
        sub="Source: Action Intelligence Engine · Rule-based estimates"
        right={<span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CONF_COLOR[conf]||CONF_COLOR.medium}`}>
          {conf} confidence
        </span>} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="text-base font-bold text-slate-900" data-testid="top-action-name">
              {ACTION_LABELS[top.action_type] || top.action_type}
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">{top.description}</p>
          </div>
          <span className="text-xs px-2 py-1 bg-slate-100 rounded-full text-slate-500 font-medium flex-shrink-0 ml-3">
            Priority {fmt(top.priority_score,0)}/100
          </span>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-4">
          {[
            {label:"CO₂ Reduction",    value:`${fmt(top.co2_reduction,1)} t/yr`,  color:"text-[#059669]"},
            {label:"Annual Savings",   value:fmtK(top.annual_savings),            color:"text-blue-600"},
            {label:"Payback",          value:`${fmt(top.payback_years,1)} yrs`,   color:"text-amber-600"},
            {label:"ROI",              value:`${fmt(top.roi_pct,0)}%`,            color:"text-indigo-600"},
          ].map(({label,value,color})=>(
            <div key={label} className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
              <p className={`text-base font-bold tabular-nums ${color}`}>{value}</p>
              <p className="text-xs text-slate-400 uppercase tracking-wide mt-0.5">{label}</p>
            </div>
          ))}
        </div>
        {notes && (
          <div className="bg-slate-50 rounded-lg p-3 mb-4">
            <p className="text-xs font-medium text-slate-500 mb-1">Assumption Summary</p>
            <p className="text-xs text-slate-500 leading-relaxed">{notes.slice(0,200)}{notes.length>200?"...":""}</p>
          </div>
        )}
        <button onClick={() => navigate(`/assessments/${assessmentId}/actions`)}
          data-testid="view-action-plan-btn"
          className="text-xs text-[#059669] font-medium flex items-center gap-1 hover:text-emerald-700">
          View Full Action Plan <ArrowRight size={11} />
        </button>
      </div>
    </Card>
  );
}

// ── SME Journey Status Panel (replaces empty dashboard for journey users) ─
function SMEJourneyPanel({ journeyData, navigate }) {
  const fp  = journeyData?.footprint;
  const opp = journeyData?.opportunity;
  const act = journeyData?.actions;
  const cars = journeyData?.cars_score;
  const raw = journeyData?.raw_inputs;

  // Format last updated
  const lastUpdated = raw?.last_updated
    ? new Date(raw.last_updated).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })
    : null;

  const cats = (fp?.by_category || []).filter(c => c.scope !== "intensity_only" && c.kgco2e > 0)
    .sort((a, b) => b.kgco2e - a.kgco2e);
  const total = fp?.total_tco2e || 0;

  return (
    <div className="space-y-4" data-testid="sme-journey-panel">
      {/* Header + Update CTA */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Your Carbon Footprint</p>
          <p className="text-xs text-slate-400 mt-0.5">{lastUpdated ? `Last updated ${lastUpdated}` : "Based on your submitted data"}</p>
        </div>
        <button
          onClick={() => navigate("/sme-journey")}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700"
          data-testid="update-data-btn">
          <Edit3 size={13} /> Update Data
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SectionCard>

          <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
            <TrendingDown size={14} className="text-slate-600" />
          </div>
          <p className="text-3xl font-bold text-slate-900 tabular-nums leading-none">{total || "—"}</p>
          <p className="text-xs text-slate-400 mt-1">tCO₂e / year</p>
          <p className="text-xs text-slate-300 uppercase tracking-wide mt-0.5">Carbon Footprint</p>
        
        </SectionCard>
        <SectionCard>

          <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center mb-3">
            <Target size={14} className="text-teal-600" />
          </div>
          <p className="text-3xl font-bold text-teal-600 tabular-nums leading-none">{act?.action_count || "—"}</p>
          <p className="text-xs text-slate-400 mt-1">opportunities found</p>
          <p className="text-xs text-slate-300 uppercase tracking-wide mt-0.5">Actions</p>
        
        </SectionCard>
        <SectionCard>
          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center mb-3">
            <DollarSign size={14} className="text-emerald-600" />
          </div>
          <p className="text-3xl font-bold text-emerald-600 tabular-nums leading-none">
            {opp?.vcm_value_low ? `$${Math.round(opp.vcm_value_low / 1000)}k+` : "—"}
          </p>
          <p className="text-xs text-slate-400 mt-1">carbon value / year</p>
          <p className="text-xs text-slate-300 uppercase tracking-wide mt-0.5">Opportunity</p>
        </SectionCard>
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center mb-3">
            <Shield size={14} className="text-amber-600" />
          </div>
          <p className="text-3xl font-bold text-amber-600 tabular-nums leading-none">
            {cars?.score ? Math.round(cars.score) : "—"}
          </p>
          <p className="text-xs text-slate-400 mt-1">{cars?.label || "Climate Readiness"}</p>
          <p className="text-xs text-slate-300 uppercase tracking-wide mt-0.5">
            {cars?.band ? `Band ${cars.band} · CRS` : "Readiness Score"}
          </p>
        </div>
      </div>

      {/* Emissions breakdown + key insight */}
      {cats.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-900">Emissions Breakdown</h3>
            <button onClick={() => navigate("/sme-journey")}
              className="text-xs text-teal-600 font-medium hover:underline flex items-center gap-1">
              View full journey <ArrowRight size={11} />
            </button>
          </div>
          <div className="space-y-2 mb-4">
            {cats.map(cat => {
              const pct = Math.round(cat.kgco2e / (total * 1000) * 100);
              const friendly = {
                "Fleet Diesel (Scope 1)":     "Fleet vehicles",
                "Generator Diesel (Scope 1)": "Generator fuel",
              };
              const label = friendly[cat.label] || cat.label.replace(/\s*\(.*\)/, "");
              return (
                <div key={cat.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600">{label}</span>
                    <span className="font-bold text-slate-900">{Math.round(cat.kgco2e/1000*10)/10}t · {pct}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-teal-500 transition-all duration-700"
                      style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Key insight + CTA */}
          <div className="bg-[#0F172A] rounded-xl px-4 py-3 flex items-center justify-between gap-4">
            <p className="text-xs text-slate-300">
              {journeyData?.what_this_means?.biggest_source || `Your footprint is ${total} tCO₂e/yr.`}
            </p>
            <button onClick={() => navigate("/sme-journey")}
              className="flex-shrink-0 px-3 py-2 bg-teal-600 text-white text-xs font-bold rounded-lg hover:bg-teal-500">
              Continue Journey
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Journey Start Banner (no assessments) ────────────────────────────────
const JOURNEY_STEPS = [
  { n: 1, label: "Set Up Profile",          done: true  },
  { n: 2, label: "Measure Emissions",       done: false },
  { n: 3, label: "Find Opportunities",      done: false },
  { n: 4, label: "Discover Carbon Value",   done: false },
  { n: 5, label: "Choose Your Path",        done: false },
];

function JourneyStartBanner({ navigate, orgName, userName }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="max-w-2xl mx-auto pt-8" data-testid="journey-start-banner">
      
      <PageHeader
        label="Your Carbon Footprint"
        title={`${greeting}${userName ? `, ${userName}` : ""}`}
      />

      {/* Primary action */}
      <div className="bg-[#0F172A] rounded-xl p-6 mb-6">
        <p className="text-teal-400 text-xs font-bold uppercase tracking-widest mb-1">Step 2 of 5</p>
        <h2 className="text-white text-lg font-bold mb-1">Measure Your Emissions</h2>
        <p className="text-slate-400 text-sm mb-4">
          Once you know your baseline, we can show you exactly where your carbon revenue opportunity is.
        </p>
        <button onClick={() => navigate("/assessments")}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#059669] text-white text-sm font-semibold rounded-xl hover:bg-emerald-700"
          data-testid="start-assessment-btn">
          <span>Start Climate Assessment</span>
          <span className="text-emerald-300 text-xs">→</span>
        </button>
      </div>

      {/* Journey progress */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-bold text-slate-900">Your Journey</p>
          <p className="text-xs text-slate-400">20% complete</p>
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded-full mb-5 overflow-hidden">
          <div className="h-full w-[20%] bg-[#059669] rounded-full" />
        </div>
        <div className="space-y-2">
          {JOURNEY_STEPS.map(s => (
            <div key={s.n} className={`flex items-center gap-3 text-xs ${s.done ? "text-emerald-600" : "text-slate-400"}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${s.done ? "bg-emerald-100 text-emerald-600" : s.n === 2 ? "bg-teal-600 text-white" : "bg-slate-100"}`}>
                {s.done ? "✓" : s.n}
              </span>
              <span className={s.n === 2 ? "font-bold text-slate-900" : ""}>{s.label}</span>
              {s.n === 2 && <span className="ml-auto text-xs font-semibold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">Next</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Or view the demo */}
      <p className="text-center text-xs text-slate-400 mt-4">
        Want to see what's possible first?{" "}
        <button onClick={() => navigate("/sme-journey")} className="text-teal-600 font-semibold hover:underline">
          View the Gasavant Carbon Opportunity Demo →
        </button>
      </p>
    </div>
  );
}

// ── Journey Progress Bar (dashboard header for active users) ─────────────
function JourneyProgressBar({ hasBaseline, hasActions, hasCars, navigate }) {
  const steps = [
    { label: "Profile",     done: true },
    { label: "Emissions",   done: !!hasBaseline },
    { label: "Opportunities",done: !!hasActions && hasActions > 0 },
    { label: "Value",       done: !!hasCars },
    { label: "Monetize",    done: false },
  ];
  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  if (pct === 100) return null; // no progress bar when fully complete

  const nextStep = steps.find(s => !s.done);
  const nextLabel = nextStep?.label === "Opportunities" ? "Find reduction opportunities" :
                    nextStep?.label === "Value" ? "Discover your carbon value" :
                    nextStep?.label === "Monetize" ? "Choose your monetization path" :
                    "Continue your journey";

  return (
    <div className="bg-teal-600 rounded-xl px-5 py-4 flex items-center gap-4" data-testid="journey-progress-bar">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-white text-xs font-bold">Next: {nextLabel}</p>
          <p className="text-teal-200 text-xs">{pct}% complete</p>
        </div>
        <div className="h-1.5 bg-teal-800 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center gap-3 mt-2">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-1 text-xs">
              <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${s.done ? "bg-white text-teal-600" : "bg-teal-800 text-teal-400"}`}>
                {s.done ? "✓" : ""}
              </span>
              <span className={s.done ? "text-white" : "text-teal-400"}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>
      <button onClick={() => navigate("/sme-journey")}
        className="flex-shrink-0 px-3 py-2 bg-white text-teal-700 text-xs font-bold rounded-xl hover:bg-teal-50">
        View Journey
      </button>
    </div>
  );
}



// ── SECTION H: CTA Strip ─────────────────────────────────────────────────
function DashboardCtaStrip({ assessmentId, navigate }) {
  return (
    <div className="bg-[#0F172A] rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="cta-strip">
      <div>
        <p className="text-white font-semibold text-sm">Ready to act on your climate data?</p>
        <p className="text-slate-400 text-xs mt-0.5">Turn emissions insight into a finance-ready investment case.</p>
      </div>
      <div className="flex gap-3 flex-shrink-0">
        <button onClick={() => navigate(`/assessments/${assessmentId}/actions`)}
          data-testid="cta-view-actions"
          className="px-4 py-2.5 border border-slate-700 text-white text-sm font-medium rounded-xl hover:bg-slate-800 transition-colors flex items-center gap-2">
          <Sparkles size={14} /> View Actions
        </button>
        <button onClick={() => navigate(`/assessments/${assessmentId}/actions`)}
          data-testid="cta-generate-pack"
          className="px-4 py-2.5 bg-[#059669] text-white text-sm font-medium rounded-xl hover:bg-emerald-600 transition-colors flex items-center gap-2">
          <Package size={14} /> Generate Investment Pack
        </button>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user, currentOrg, activeRole } = useAuth();
  const { progressLevel, hasEmissions, hasActions, journeyData } = useJourney();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [baseline, setBaseline]       = useState(null);
  const [confidence, setConfidence]   = useState(null);
  const [actions, setActions]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadingData, setLoadingData] = useState(false);

  // Load assessments list — skip for SME (uses journey system)
  useEffect(() => {
    if (activeRole === "sme" || !currentOrg) { setLoading(false); return; }
    client.get(`/assessments?org_id=${currentOrg.id}`)
      .then(r => {
        const list = r.data || [];
        setAssessments(list);
        const best = list.find(a=>a.status==="issued") || list.find(a=>a.status==="approved") ||
                     list.find(a=>a.status==="analysis") || list.find(a=>a.status==="review") ||
                     list.find(a=>a.status==="calculated");
        if (best) setSelectedId(best.id);
        else setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [currentOrg, activeRole]);

  // Load data for selected assessment
  const loadDashboardData = useCallback(async (id) => {
    if (!id) return;
    setLoadingData(true);
    try {
      const [bRes, cRes, aRes] = await Promise.allSettled([
        client.get(`/baseline/assessments/${id}`),
        client.get(`/confidence/assessments/${id}/score`),
        client.get(`/actions/assessments/${id}`),
      ]);
      if (bRes.status === "fulfilled") setBaseline(bRes.value.data);
      if (cRes.status === "fulfilled") setConfidence(cRes.value.data);
      if (aRes.status === "fulfilled") setActions(aRes.value.data?.actions || []);
    } catch {}
    setLoading(false);
    setLoadingData(false);
  }, []);

  useEffect(() => {
    if (selectedId) loadDashboardData(selectedId);
  }, [selectedId, loadDashboardData]);

  // ── HARD ADMIN GUARD ──────────────────────────────────────────────────────
  // Admin users must NEVER render the project/SME dashboard.
  // Placed after all hooks to comply with React Rules of Hooks.
  if (user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  // For SME users: render the clean SME Dashboard (all hooks declared above)
  if (activeRole === "sme") {
    return <SMEDashboard />;
  }

  const energy  = calcEnergyCost(baseline);
  const totalRed= actions.reduce((s,a)=>s+(a.co2_reduction||0), 0);
  const hour    = new Date().getHours();
  const greeting= hour<12?"Good morning":hour<17?"Good afternoon":"Good evening";

  if (loading) return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="h-8 w-64 bg-slate-200 rounded animate-pulse" />
      <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_,i)=><div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
      <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
    </div>
  );

  // No org state
  if (!currentOrg) return (
    <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-4">
          <TrendingDown size={28} className="text-slate-400" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">Complete Your Setup</h2>
        <p className="text-sm text-slate-500 mb-4">Set up your organization to start tracking emissions.</p>
        <button onClick={() => navigate("/onboarding")} className="px-4 py-2 bg-[#0F172A] text-white text-sm font-medium rounded-xl">Set Up Organization</button>
      </div>
    </div>
  );

  // No assessments state — show journey panel if SME has data, otherwise start banner
  if (assessments.length === 0) return (
    <div className="p-6 lg:p-8 min-h-screen bg-[#F8FAFC]">
      <div className="">
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">{currentOrg?.name}</p>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            {new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening"}
            {user?.first_name ? `, ${user.first_name}` : ""}
          </h1>
        </div>
        {user?.role !== "admin" && <ActivationBanner />}
        {activeRole === "sme" && journeyData?.footprint ? (
          <SMEJourneyPanel journeyData={journeyData} navigate={navigate} />
        ) : (
          <JourneyStartBanner navigate={navigate} orgName={currentOrg?.name} userName={user?.first_name} />
        )}
      </div>
    </div>
  );

  const calcAssessments = assessments.filter(a=>["analysis","review","approved","issued"].includes(a.status));

  return (
    <div className="p-6 lg:p-8 space-y-6 min-h-screen" data-testid="sme-dashboard">
      {/* Activation Banner — shown to non-admin users only */}
      {user?.role !== "admin" && <ActivationBanner />}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-slate-400">{currentOrg?.name}</p>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight tracking-tight">{greeting}, {user?.first_name}</h1>
        </div>
        <div className="flex items-center gap-3">
          {calcAssessments.length > 1 && (
            <select value={selectedId||""} onChange={e=>setSelectedId(Number(e.target.value))}
              data-testid="assessment-selector"
              className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#059669]">
              {calcAssessments.map(a=>(
                <option key={a.id} value={a.id}>{a.title} {a.reporting_year?`(FY${a.reporting_year})`:""}</option>
              ))}
            </select>
          )}
          {selectedId && <span className="text-xs text-slate-400 hidden sm:block">
            Assessment #{selectedId}
            {assessments.find(a=>a.id===selectedId)?.status ? ` · ${assessments.find(a=>a.id===selectedId).status}` : ""}
          </span>}
          <button onClick={()=>loadDashboardData(selectedId)} disabled={loadingData}
            data-testid="refresh-btn"
            className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors">
            <RefreshCw size={13} className={loadingData?"animate-spin":""} />
          </button>
          <button onClick={()=>navigate("/assessments")}
            data-testid="new-assessment-btn"
            className="flex items-center gap-2 px-3 py-2 bg-[#0F172A] text-white text-xs font-medium rounded-xl hover:bg-slate-800">
            <Plus size={13} /> New Assessment
          </button>
        </div>
      </div>

      {/* SME Journey Panel — shown when user has journey data, replaces empty sections */}
      {activeRole === "sme" && journeyData?.footprint && (
        <SMEJourneyPanel journeyData={journeyData} navigate={navigate} />
      )}

      {/* Journey Progress Bar — only shown if no journey panel */}
      {!(activeRole === "sme" && journeyData?.footprint) && (
        <JourneyProgressBar
          hasBaseline={!!baseline || hasEmissions}
          hasActions={actions.length || hasActions ? 1 : 0}
          hasCars={confidence?.overall_score}
          navigate={navigate}
        />
      )}

      {/* No calculation data banner — only shown when not in SME journey mode */}
      {!baseline && selectedId && !(activeRole === "sme" && journeyData?.footprint) && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5">
          <p className="text-sm font-bold text-amber-700 mb-1">Your emissions haven't been calculated yet.</p>
          <p className="text-xs text-amber-600 mb-3">Run your calculation to unlock:</p>
          <div className="space-y-1 mb-4">
            {["Your emission baseline (what you emit)", "Your reduction opportunities (where to act)", "Your carbon revenue potential (what it's worth)"].map(item => (
              <div key={item} className="flex items-center gap-2 text-xs text-amber-700">
                <span className="text-amber-400">→</span> {item}
              </div>
            ))}
          </div>
          <button onClick={() => navigate(`/assessments/${selectedId}`)}
            className="px-4 py-2 bg-[#059669] text-white text-xs font-semibold rounded-xl hover:bg-emerald-700"
            data-testid="run-calculation-btn">
            Run Calculations Now
          </button>
        </div>
      )}

      {/* Section A: KPI Strip — only if we have old-style baseline data OR not SME journey user */}
      {!(activeRole === "sme" && journeyData?.footprint) && (
        <SmeKpiStrip baseline={baseline} confidence={confidence} energy={energy} />
      )}

      {/* Financing Readiness Score — top-level trust signal */}
      {selectedId && baseline && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <FinancingReadinessScore assessmentId={selectedId} mode="panel" />
          </div>
          <div className="space-y-4">
            <FinancingReadinessScore assessmentId={selectedId} mode="kpi" />
          </div>
        </div>
      )}

      {/* Section B + C: Snapshot · Scope · Hotspots */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ClimateSnapshotCard baseline={baseline} />
          <HotspotsCard baseline={baseline} />
        </div>
        <ScopeBreakdownCard baseline={baseline} />
      </div>

      {/* Section D: Monthly Trend */}
      <MonthlyTrendCard baseline={baseline} actions={actions} />

      {/* Section E + F: Carbon Value + Data Confidence */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <CarbonValueCard actions={actions} />
        <DataConfidenceCard confidence={confidence} assessmentId={selectedId} navigate={navigate} />
      </div>

      {/* Section G: Action Preview */}
      <ActionPreviewCard actions={actions} assessmentId={selectedId} navigate={navigate} />

      {/* Carbon Credit Potential */}
      {selectedId && baseline && (
        <CreditPotentialPanel assessmentId={selectedId} />
      )}

      {/* Section H: CTA Strip */}
      {selectedId && <DashboardCtaStrip assessmentId={selectedId} navigate={navigate} />}
    </div>
  );
}

